import { useMemo } from 'react'

import { orderBy, zip } from 'lodash'
import { parseUnits } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  TokenMetadataWithPrice,
  useMultipleTokensMetadata,
  useTokenMetadata,
} from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { isSwapChainId } from '~/utils'
import { TokenBalance } from '~/utils/account'
import { Address, EvmAddress } from '~/utils/address'
import { isStablecoin } from '~/utils/addresses'
import { getBigNumberFromNumber, getNumberFromBigNumber } from '~/utils/numbers'
import { isVaultAssetPair, isVaultToken } from '~/utils/vault'

export const useAutoSwap = ({
  sender,
  destinationToken,
  preventBridge = false,
}: {
  sender?: IAddress
  destinationToken?: {
    token: IAddress
    chainId: SupportedChainId
    amount: string
  }
  preventBridge?: boolean
}) => {
  const { convertTokenToUSD } = useTokenPrices()

  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (account) => account.address === sender,
  )

  const { balances: balancesMap } = useMultipleSmartAccountBalances(
    smartAccount ? [smartAccount.address] : [],
  )
  const balances = useMemo(
    () =>
      smartAccount ? (balancesMap[smartAccount.address]?.balances ?? []) : [],
    [balancesMap, smartAccount],
  )

  const destinationTokenMetadata = useTokenMetadata({
    address: destinationToken?.token,
    chainId: destinationToken?.chainId,
  })

  const buyUSDValue = destinationToken
    ? convertTokenToUSD({
        token: destinationToken.token,
        amount: parseUnits(
          destinationToken.amount ?? '0',
          destinationTokenMetadata?.decimals ?? 18,
        ),
        chainId: destinationToken.chainId,
      })
    : undefined

  const tokens = useMultipleTokensMetadata(balances, { includePrice: true })

  const isDestinationVaultToken = destinationToken
    ? isVaultToken(destinationToken?.chainId, destinationToken?.token)
    : false

  return useMemo(() => {
    // If USD value not available yet, return empty lists
    if (buyUSDValue === undefined)
      return { swapTokens: [], insufficientTokens: [] }

    const candidates = zip(balances, tokens)
      .map(([balance, token]) => {
        return {
          ...balance,
          metadata: token,
        }
      })
      .filter(
        (
          balance,
        ): balance is TokenBalance & { metadata: TokenMetadataWithPrice } =>
          !!balance.metadata?.price,
      )
      .filter((balance) =>
        balance.chainId === destinationToken?.chainId
          ? isSwapChainId(balance.chainId)
          : true,
      )
      // If prevent bridge is true, only include tokens on the destination chain
      .filter((balance) =>
        preventBridge ? balance.chainId === destinationToken?.chainId : true,
      )
      // If the destination token is a vault token, only include tokens on the destination chain
      .filter((balance) =>
        isDestinationVaultToken
          ? balance.chainId === destinationToken?.chainId
          : true,
      )
      // Exclude the destination token itself — paying for a token with the
      // same token on the same chain isn't a swap, it's a direct transfer.
      .filter(
        (balance) =>
          !(
            balance.chainId === destinationToken?.chainId &&
            Address.equal(balance.address, destinationToken?.token)
          ),
      )
      .map((balance) => {
        // Earn tokens redeem directly into their underlying asset via
        // ERC-4626 — there is no swap slippage on that path, so we skip the
        // cushion. Without this, sending the full displayed Earn balance is
        // rejected by a fraction of a percent.
        const isDirectWithdrawal = isDirectVaultWithdrawal(
          { address: balance.address, chainId: balance.chainId },
          destinationToken,
        )

        const sellEstimate = isDirectWithdrawal
          ? buyUSDValue / balance.metadata.price!
          : (buyUSDValue +
              buyUSDValue /
                getSellAmountSlippage(
                  {
                    address: destinationToken?.token ?? '',
                    chainId: destinationToken?.chainId ?? 1,
                    symbol: destinationTokenMetadata?.symbol,
                  },
                  {
                    address: balance.address,
                    chainId: balance.chainId,
                    symbol: balance.metadata.symbol,
                  },
                )) /
            balance.metadata.price!

        const sellEstimateBigNumber = getBigNumberFromNumber(
          sellEstimate,
          balance.metadata.decimals,
        )

        return {
          ...balance,
          isDirectWithdrawal,
          sellEstimate: sellEstimateBigNumber,
        }
      })

    // Direct vault withdrawals use `<=` because there is no cushion to
    // protect against off-by-one — the user should be able to redeem
    // exactly their share balance. Other paths keep the strict `<` so the
    // slippage cushion stays a true safety margin.
    const hasSufficientBalance = (candidate: (typeof candidates)[number]) =>
      candidate.isDirectWithdrawal
        ? candidate.sellEstimate <= candidate.amount
        : candidate.sellEstimate < candidate.amount

    const swapTokens = orderBy(
      candidates.filter(hasSufficientBalance),
      [
        // 1) Vault tokens gets priority
        (t) => (isVaultToken(t.chainId, EvmAddress.format(t.address)) ? 0 : 1),
        // 2) Prefer cross-chain over same chain
        (t) => (t.chainId !== destinationToken?.chainId ? 0 : 1),
        // 3) Order by balance (higher first)
        (t) => t.usdValue ?? 0,
      ],
      ['asc', 'asc', 'desc'],
    )

    // Held-but-short candidates, each with how much more of the token the send
    // would need (cushion included). Ranked by the USD value of that shortfall
    // so callers can surface the cheapest top-ups first.
    const insufficientTokens = orderBy(
      candidates
        .filter((candidate) => !hasSufficientBalance(candidate))
        .map((candidate) => ({
          ...candidate,
          shortfall: candidate.sellEstimate - candidate.amount,
        }))
        .filter((candidate) => candidate.shortfall > 0n),
      [
        (t) =>
          getNumberFromBigNumber(t.shortfall, t.metadata.decimals) *
          t.metadata.price!,
      ],
      ['asc'],
    )

    return { swapTokens, insufficientTokens }
  }, [
    balances,
    tokens,
    buyUSDValue,
    destinationTokenMetadata?.symbol,
    destinationToken,
    preventBridge,
    isDestinationVaultToken,
  ])
}

/**
 * Whether the swap is a single-chain ERC-4626 redemption: vault → its own
 * underlying asset on the same chain. Used to bypass the slippage cushion
 * because `previewWithdraw` on a standard ERC-4626 vault is exact.
 *
 * `isVaultAssetPair` covers the "vault paired with this asset" lookup; we
 * add the chainId-equality guard the utility doesn't enforce.
 *
 * IMPORTANT: this assumes every entry in `VAULT_ASSET_MAPPING` is a
 * standard ERC-4626 vault with no withdrawal fee, no queue, and no
 * rebasing quirks. Aave's StaticATokenLM (the only entry today) satisfies
 * this. If a non-vanilla vault is added, gate the bypass behind a
 * per-vault flag instead.
 */
const isDirectVaultWithdrawal = (
  sellToken: { address: IAddress; chainId: SupportedChainId },
  destinationToken: { token: IAddress; chainId: SupportedChainId } | undefined,
): boolean => {
  if (!destinationToken || sellToken.chainId !== destinationToken.chainId)
    return false
  return isVaultAssetPair({
    chainId: sellToken.chainId,
    vault: sellToken.address,
    asset: destinationToken.token,
  })
}

const getSellAmountSlippage = (
  buyToken: { address: string; chainId: number; symbol: string | undefined },
  sellToken: { address: string; chainId: number; symbol: string | undefined },
) => {
  if (!buyToken.symbol || !sellToken.symbol) return 200 // default 0.5% when no symbol
  if (buyToken.symbol === sellToken.symbol) return 2000 // 0.05% for bridge between same assets
  if (
    isStablecoin(buyToken.address, buyToken.chainId) &&
    isStablecoin(sellToken.address, sellToken.chainId)
  )
    return 2000 // 0.05% for swaps between stables
  if (
    isStablecoin(buyToken.address, buyToken.chainId) &&
    sellToken.symbol === 'ETH'
  )
    return 2000 // 0.05% for swaps between stable coin and ETH
  if (
    buyToken.symbol === 'ETH' &&
    isStablecoin(sellToken.address, sellToken.chainId)
  )
    return 2000 // 0.05% for swaps between stable coin and ETH
  return 200 // default 0.5%
}
